import { useEffect, useRef } from "react";
import { Mixpanel } from "lib/mixpanel/mixpanel";

import type { BriefingReportPageData } from "content-ui/briefings/fetchers/briefingReportFetcher";

import Heading from "content-ui/briefings/components/briefingsReports/elements/heading";
import CardContainer from "content-ui/briefings/components/briefingsReports/elements/cardContainer";
import { MarkdownTracked } from "components/markdownTracked/markdownTracked";
import { MIXPANEL_TOKEN } from "lib/constants";
import { useUserData } from "lib/store/user";

const PinIcon = () => {
  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.67188 2.04443C2.58854 1.12777 3.69271 0.669434 4.98438 0.669434C6.27604 0.669434 7.38021 1.12777 8.29688 2.04443C9.21354 2.94027 9.67188 4.03402 9.67188 5.32568C9.67188 5.97152 9.50521 6.72152 9.17188 7.57568C8.85938 8.40902 8.47396 9.19027 8.01562 9.91943C7.55729 10.6278 7.09896 11.3049 6.64062 11.9507C6.18229 12.5757 5.79688 13.0757 5.48438 13.4507L4.98438 13.9819C4.85938 13.8569 4.69271 13.6694 4.48438 13.4194C4.27604 13.1694 3.90104 12.6903 3.35938 11.9819C2.83854 11.2528 2.36979 10.5549 1.95312 9.88818C1.53646 9.20068 1.16146 8.42985 0.828125 7.57568C0.494792 6.72152 0.328125 5.97152 0.328125 5.32568C0.328125 4.03402 0.776042 2.94027 1.67188 2.04443ZM3.79688 6.51318C4.13021 6.82568 4.52604 6.98193 4.98438 6.98193C5.44271 6.98193 5.83854 6.82568 6.17188 6.51318C6.50521 6.17985 6.67188 5.78402 6.67188 5.32568C6.67188 4.86735 6.50521 4.48193 6.17188 4.16943C5.83854 3.8361 5.44271 3.66943 4.98438 3.66943C4.52604 3.66943 4.13021 3.8361 3.79688 4.16943C3.48438 4.48193 3.32812 4.86735 3.32812 5.32568C3.32812 5.78402 3.48438 6.17985 3.79688 6.51318Z"
        fill="black"
      />
    </svg>
  );
};

const FlagIcon = () => {
  return (
    <svg
      width="10"
      height="13"
      viewBox="0 0 10 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.28125 2.32568H10V8.98193H5.34375L5.0625 7.66943H1.34375V12.3257H0V0.981934H6L6.28125 2.32568Z"
        fill="black"
      />
    </svg>
  );
};

const StarsCard = ({
  briefing,
  id,
}: {
  briefing: BriefingReportPageData;
  id: string;
}) => {
  const mixpanelRef = useRef<Mixpanel | null>(null);
  const { userId } = useUserData();

  useEffect(() => {
    mixpanelRef.current = new Mixpanel(MIXPANEL_TOKEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CardContainer id={`content__${id}`}>
      <Heading text={briefing.risingStarsHeading} id={id} />
      <p className="text-[16px] md:text-[21px]">
        {briefing.risingStarsDescription}
      </p>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-y-6 lg:gap-y-8">
        {briefing.risingStars.map((risingStar, i) => (
          <div
            key={i}
            className="flex w-100 max-w-[370px] flex-col justify-between justify-self-center rounded-2xl border-[3px] border-black"
          >
            <div className="grid grid-cols-[2fr_1fr] border-b-[1px] border-black">
              <div className="flex flex-col border-b-[1px] border-r-[1px] border-black px-[14px] py-[10px] md:py-[14px]">
                {risingStar.companyUrl ? (
                  <a
                    href={risingStar.companyUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      if (userId && mixpanelRef.current) {
                        mixpanelRef.current.track("Link Click", {
                          "Link Clicked": risingStar.companyUrl,
                          distinct_id: userId,
                        });
                      }
                    }}
                    className="text-[20px] font-[700] text-cerise"
                  >
                    {risingStar.name}
                  </a>
                ) : (
                  <p className="text-[20px] font-[700]">{risingStar.name}</p>
                )}
                <p className="text-[14px] font-[700]">
                  {risingStar.subheadingLineOne}
                </p>
                <p className="text-[14px]">{risingStar.subheadingLineTwo}</p>
              </div>

              <div className="flex flex-col justify-around rounded-tr-2xl border-b-[1px] border-black bg-[#E1EAFD] px-[14px] py-[10px] text-right md:py-[14px]">
                <p className="text-[14px] font-[700] text-[#5c615e]">
                  Total funding
                </p>
                <p className="text-[20px] font-[700]">
                  {risingStar.totalFunding}
                </p>
              </div>

              <div className="flex justify-start self-center border-r-[1px] border-black px-[14px] py-2 text-[15px] font-[700]">
                <span className="mr-[6px] inline-block self-center">
                  <PinIcon />
                </span>
                {risingStar?.city}
                {risingStar?.city && risingStar?.country && ", "}
                {risingStar?.country}
              </div>
              <div className="flex justify-end self-center px-[14px] py-2 text-[15px] font-[700]">
                <span className="mr-[6px] inline-block self-center">
                  <FlagIcon />
                </span>
                {risingStar.founded}
              </div>
            </div>

            <div className="h-100">
              <MarkdownTracked
                text={risingStar.description}
                className="m-3 list-disc text-[14px] md:text-[16px] lg:m-4 lg:my-5 lg:mr-2"
                component="Rising Stars"
              />
            </div>

            <div className="flex flex-col rounded-b-2xl border-t-[1px] border-black bg-[#E1EAFD] font-[700]">
              <div className="flex">
                <div className="flex w-100 flex-col px-[14px] py-[10px] md:py-[14px]">
                  <p className="text-[16px] text-[#5c615e]">Round</p>
                  <p className="text-[20px]">{risingStar.lastRoundStage}</p>
                </div>
                {risingStar.valuation && (
                  <div className="flex w-100 flex-col border-l-[1px] border-black px-[14px] py-[10px] text-right md:py-[14px]">
                    <p className="text-[16px] text-[#5c615e]">Valuation</p>
                    <p className="text-[20px]">{risingStar.valuation}</p>
                  </div>
                )}
              </div>

              <hr className="border-black" />

              <div className="flex">
                <div className="flex w-100 flex-col px-[14px] py-[10px] md:py-[14px]">
                  <p className="text-[16px] text-[#5c615e]">Date</p>
                  <p className="text-[20px]">{risingStar.lastRoundDate}</p>
                </div>
                <div className="flex w-100 flex-col border-l-[1px] border-black px-[14px] py-[10px] text-right md:py-[14px]">
                  <p className="text-[16px] text-[#5c615e]">Size</p>
                  <p className="text-[20px]">{risingStar.lastRoundSize}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </CardContainer>
  );
};

export default StarsCard;
